import TableHeader from 'components/TableHeader/TableHeader';
import CustomTable from 'components/CustomTable/CustomTable';
import classNames from 'classnames';
import React from 'react';
import { translate } from 'components/Translation';
import PhoneOutlined from '@material-ui/icons/PhoneOutlined';
import { BodyM, BodyMBold } from 'components/Typography/Typography';
import { SingleAccount } from 'utils/api/models/accounts/singleAccount';
import { DepartmentsCell, TextCell, UserNameCell } from 'components/Cells/Cells';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import Business from '@material-ui/icons/Business';
import styles from 'assets/jss/material-dashboard-pro-react/views/payments/topupStyle';
import { makeStyles } from '@material-ui/styles';

// @ts-ignore
const useStyles = makeStyles(styles);

interface INumbersTableProps {
	searchValue: types.topUp.IFilter<string, undefined>;
	setSearchValue: React.Dispatch<React.SetStateAction<types.topUp.IFilter<string, undefined>>>;
	filterBy: keyof types.topUp.IFilter<string, undefined>;
	departmentsFetched: { [key: string]: SingleDepartment };
	filtered: types.topUp.IFilter<SingleAccount[], SingleDepartment[]>;
	selected: types.topUp.IFilter<string[], undefined>;
	setSelected: React.Dispatch<React.SetStateAction<types.topUp.IFilter<string[], undefined>>>;
}

const NumbersTable = ({
	searchValue,
	setSearchValue,
	filterBy,
	departmentsFetched,
	filtered,
	selected,
	setSelected,
}: INumbersTableProps) => {
	const classes = useStyles();

	const placeholder = {
		departments: translate('payments.topup.departments'),
		numbers: translate('payments.topup.numbers'),
	}[filterBy];

	const columns = {
		numbers: [
			{
				Header: translate('phoneNumber'),
				accessor: 'accountNum',
				width: 150,
				// eslint-disable-next-line react/display-name
				Cell: ({ original: { accountNum = '' } }) => {
					let index = accountNum.indexOf(searchValue.numbers);
					const isFound = searchValue.numbers !== '' && accountNum.includes(searchValue.numbers);
					return (
						<span>
							<PhoneOutlined className={classes.numbersPhone} />
							{isFound && index !== 0 && <BodyM inline>{accountNum.slice(0, index)}</BodyM>}
							{isFound && (
								<BodyMBold inline color="brand">
									{accountNum.slice(index, index + searchValue.numbers.length)}
								</BodyMBold>
							)}
							{
								<BodyM inline>
									{isFound ? accountNum.slice(index + searchValue.numbers.length) : accountNum}
								</BodyM>
							}
						</span>
					);
				},
			},
			{
				Header: translate('payments.topup.userName'),
				accessor: 'login',
				width: 180,
				Cell: ({ original }: { original: SingleAccount }) => (
					<UserNameCell text={original.login} type={original.userType} />
				),
			},
			{
				Header: translate('payments.topup.department'),
				width: 150,
				Cell: (props: { original: SingleAccount }) => {
					const {
						original: { deptId },
					} = props;
					const dept = departmentsFetched[deptId];
					return (
						<DepartmentsCell departments={dept ? [dept] : []} maxNumberOfBadges={1} maxBadgeWidth={150} />
					);
				},
			},
		],
		departments: [
			{
				Header: translate('payments.topup.departments'),
				Cell: ({ original }: { original: SingleDepartment }) => (
					<span className={classes.departmentName}>
						<Business className={classes.departmentIcon} style={{ backgroundColor: original.uiColor }} />
						<BodyM inline disableMargin>
							{original.name}
						</BodyM>
					</span>
				),
			},
			{
				Header: translate('payments.topup.numbersAmount'),
				accessor: 'numbers.length',
				Cell: ({ original: { numbers = [] } }) => <TextCell text={String(numbers.length)} />,
			},
		],
	};

	return (
		<>
			<TableHeader
				searchInputValue={searchValue[filterBy]}
				setSearchInputValue={(value) =>
					setSearchValue((searchValue) => ({
						...searchValue,
						[filterBy]: value,
					}))
				}
				label={placeholder}
			/>
			<CustomTable
				withCheckbox
				withHeader
				total={filtered[filterBy].length}
				selected={selected[filterBy]}
				onSelect={(value) => setSelected((selected) => ({ ...selected, [filterBy]: value }))}
				tableProps={{
					data: filtered[filterBy],
					columns: columns[filterBy],
					defaultPageSize: 5,
					getTrProps: (_state, rowInfo) => {
						const props: { className?: string } = {};

						if (rowInfo && selected[filterBy].includes(rowInfo.index)) {
							props.className = classNames(props.className, classes.selectedRow);
						}
						return props;
					},
				}}
			/>
		</>
	);
};

export default NumbersTable;
